import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Headphones, Clock, Star, Play, Search, ChevronLeft, Zap, TrendingUp, Users, Download, BookOpen, Music, AlertCircle } from 'lucide-react'
import { getAudioFiles } from '../utils/audioFolder'

// Import your podcasts to check which ones have audio
import { podcasts } from '../data/mockData'

// Series with specific episode IDs
const featuredSeries = [
  {
    id: 1,
    title: "AI Revolution Series",
    description: "From fundamentals to breakthroughs - comprehensive exploration of artificial intelligence",
    category: "Technology & AI",
    episodeCount: 5,
    totalDuration: "3.9 hours",
    rating: 4.9,
    color: "from-blue-500 to-purple-600",
    image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=250&fit=crop",
    episodes: [204, 216, 217, 258, 259], // Episode IDs from mockData
  },
  {
    id: 2,
    title: "Digital Psychology Series",
    description: "How technology is reshaping our minds, habits, and social connections",
    category: "Personal Development",
    episodeCount: 6,
    totalDuration: "5.2 hours",
    rating: 4.8,
    color: "from-purple-500 to-pink-600",
    image: "https://images.unsplash.com/photo-1543286386-713bdd548da4?w=400&h=250&fit=crop",
    episodes: [263, 289, 291, 319, 324, 328],
  },
  {
    id: 3,
    title: "Future Tech Deep Dives",
    description: "Cutting-edge technologies that are reshaping our world tomorrow",
    category: "Science & Innovation",
    episodeCount: 5,
    totalDuration: "4.3 hours",
    rating: 4.7,
    color: "from-teal-500 to-green-600",
    image: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=400&h=250&fit=crop",
    episodes: [203, 205, 223, 246, 262],
  },
  {
    id: 4,
    title: "Creative Science Series",
    description: "Where art, science, and innovation collide in unexpected ways",
    category: "Creative Arts",
    episodeCount: 5,
    totalDuration: "4.8 hours",
    rating: 4.8,
    color: "from-orange-500 to-red-600",
    image: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=250&fit=crop",
    episodes: [235, 266, 275, 287, 329],
  },
  {
    id: 5,
    title: "Health & Wellness Science",
    description: "Evidence-based approaches to physical and mental wellbeing",
    category: "Health & Wellness",
    episodeCount: 5,
    totalDuration: "4.1 hours",
    rating: 4.7,
    color: "from-green-500 to-emerald-600",
    image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=250&fit=crop",
    episodes: [252, 254, 257, 307, 316],
  },
  {
    id: 6,
    title: "Business & Creator Economy",
    description: "Modern entrepreneurship and the new rules of digital business",
    category: "Business & Entrepreneurship",
    episodeCount: 5,
    totalDuration: "4.5 hours",
    rating: 4.8,
    color: "from-indigo-500 to-blue-600",
    image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&h=250&fit=crop",
    episodes: [201, 224, 229, 260, 265],
  },
  {
    id: 7,
    title: "Everyday Science Mysteries",
    description: "Solving life's little puzzles with rigorous scientific investigation",
    category: "Science & Innovation",
    episodeCount: 5,
    totalDuration: "3.5 hours",
    rating: 4.6,
    color: "from-yellow-500 to-orange-600",
    image: "https://images.unsplash.com/photo-1517976547714-720226b864c1?w=400&h=250&fit=crop",
    episodes: [214, 277, 280, 322, 272],
  },
  {
    id: 8,
    title: "Digital Life & Identity",
    description: "Navigating the complexities of our online selves and communities",
    category: "Personal Development",
    episodeCount: 5,
    totalDuration: "4.9 hours",
    rating: 4.7,
    color: "from-cyan-500 to-blue-500",
    image: "https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=400&h=250&fit=crop",
    episodes: [288, 293, 304, 305, 311],
  }
];

function SeriesList() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [audioFiles, setAudioFiles] = useState([])
  const [seriesWithStats, setSeriesWithStats] = useState([])
  
  const categories = ['All', 'Technology & AI', 'Personal Development', 'Science & Innovation', 
                     'Creative Arts', 'Health & Wellness', 'Business & Entrepreneurship']

  // Get audio files on mount
  useEffect(() => {
    const files = getAudioFiles()
    setAudioFiles(files)
  }, [])

  // Calculate series stats with audio availability
  useEffect(() => {
    const updatedSeries = featuredSeries.map(series => {
      // Get episodes for this series
      const seriesEpisodes = series.episodes.map(id => 
        podcasts.find(p => p.id === id)
      ).filter(Boolean)

      // Calculate how many episodes have audio
      const episodesWithAudio = seriesEpisodes.filter(episode => {
        const filename = episode.audioUrl?.split('/').pop() || ''
        return audioFiles.includes(filename)
      })

      // Calculate percentage of episodes with audio
      const completionPercentage = seriesEpisodes.length > 0 
        ? Math.round((episodesWithAudio.length / seriesEpisodes.length) * 100)
        : 0

      // Determine if series is "Ready", "Partial", or "Coming Soon"
      let status = 'coming-soon'
      if (completionPercentage === 100) {
        status = 'ready'
      } else if (completionPercentage > 0) {
        status = 'partial'
      }

      return {
        ...series,
        episodesWithAudio: episodesWithAudio.length,
        totalEpisodes: seriesEpisodes.length,
        completionPercentage,
        status,
        statusText: status === 'ready' ? 'Ready Now' : 
                   status === 'partial' ? `Partially Ready (${episodesWithAudio.length}/${seriesEpisodes.length})` : 
                   'Coming Soon'
      }
    })

    setSeriesWithStats(updatedSeries)
  }, [audioFiles])

  const filteredSeries = seriesWithStats.filter(series => {
    const matchesSearch = series.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         series.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'All' || series.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  // Sort series: Ready first, then Partial, then Coming Soon
  const sortedSeries = [...filteredSeries].sort((a, b) => {
    const statusOrder = { 'ready': 0, 'partial': 1, 'coming-soon': 2 }
    return statusOrder[a.status] - statusOrder[b.status]
  })

  // Stats for the hero section - UPDATED with real data
  const seriesStats = {
    totalSeries: seriesWithStats.length,
    totalEpisodes: seriesWithStats.reduce((sum, series) => sum + series.totalEpisodes, 0),
    episodesWithAudio: seriesWithStats.reduce((sum, series) => sum + series.episodesWithAudio, 0),
    readySeries: seriesWithStats.filter(s => s.status === 'ready').length,
    totalDuration: "35+ hours",
    averageRating: 4.8
  }

  // Get status color and icon
  const getStatusInfo = (status) => {
    switch(status) {
      case 'ready':
        return {
          color: 'bg-green-100 text-green-700 border-green-300',
          icon: <Music className="w-3 h-3" />,
          text: 'Ready Now'
        }
      case 'partial':
        return {
          color: 'bg-amber-100 text-amber-700 border-amber-300',
          icon: <AlertCircle className="w-3 h-3" />,
          text: 'Partially Ready'
        }
      default:
        return {
          color: 'bg-gray-100 text-gray-700 border-gray-300',
          icon: <AlertCircle className="w-3 h-3" />,
          text: 'Coming Soon'
        }
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Enhanced Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          {/* Back Button - Moved above hero */}
          <button
            onClick={() => navigate('/podcasts')}
            className="flex items-center space-x-2 text-primary hover:text-primary/80 mb-6"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Back to Podcasts</span>
          </button>

          {/* Hero Card */}
          <div className="bg-gradient-to-br from-primary to-accent rounded-2xl p-8 md:p-12 text-white mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Podcast Series Library
            </h1>
            <p className="text-xl text-purple-100 mb-8 max-w-3xl">
              Dive deep into curated collections of podcast episodes. Each series is designed to take you on a comprehensive learning journey across specific topics.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 mb-8">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <Headphones className="w-6 h-6" />
                </div>
                <p className="text-2xl md:text-3xl font-bold">{seriesStats.totalSeries}</p>
                <p className="text-sm text-purple-100">Series</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <Play className="w-6 h-6" />
                </div>
                <p className="text-2xl md:text-3xl font-bold">{seriesStats.totalEpisodes}</p>
                <p className="text-sm text-purple-100">Episodes</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <Music className="w-6 h-6" />
                </div>
                <p className="text-2xl md:text-3xl font-bold">{seriesStats.episodesWithAudio}</p>
                <p className="text-sm text-purple-100">Ready Now</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <Clock className="w-6 h-6" />
                </div>
                <p className="text-2xl md:text-3xl font-bold">{seriesStats.totalDuration}</p>
                <p className="text-sm text-purple-100">Content</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <Star className="w-6 h-6 fill-current" />
                </div>
                <p className="text-2xl md:text-3xl font-bold">{seriesStats.averageRating}</p>
                <p className="text-sm text-purple-100">Avg Rating</p>
              </div>
            </div>

            {/* Key Features */}
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center space-x-2 bg-white/30 px-4 py-2 rounded-full">
                <Zap className="w-4 h-4" />
                <span className="font-medium">Curated Learning Paths</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/30 px-4 py-2 rounded-full">
                <BookOpen className="w-4 h-4" />
                <span className="font-medium">Comprehensive Topics</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/30 px-4 py-2 rounded-full">
                <TrendingUp className="w-4 h-4" />
                <span className="font-medium">Progressive Difficulty</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/30 px-4 py-2 rounded-full">
                <Download className="w-4 h-4" />
                <span className="font-medium">Offline Listening</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Browse All Series</h2>
          <p className="text-gray-600">Filter and search through our complete collection of podcast series</p>
          
          {/* Series Status Legend */}
          <div className="flex items-center gap-4 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span>Ready Now (All episodes have audio)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-400"></div>
              <span>Partially Ready (Some episodes have audio)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gray-400"></div>
              <span>Coming Soon (No audio yet)</span>
            </div>
          </div>
        </div>

        {/* Search and Filter - PERFECTLY ALIGNED */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Series
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by title, description, or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Category Filter */}
            <div className="w-full md:w-64">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <div className="relative">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent appearance-none"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                {/* Dropdown arrow */}
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          {/* Results Count and Clear Filters */}
          <div className="flex items-center justify-between pt-4 mt-4 border-t">
            <div>
              <p className="text-gray-600 text-sm">
                Showing <span className="font-semibold">{sortedSeries.length}</span> series
                {selectedCategory !== 'All' && ` in ${selectedCategory}`}
              </p>
              <div className="flex items-center gap-4 mt-2 text-xs">
                <div className="flex items-center gap-1 text-green-600">
                  <span className="font-medium">{seriesStats.readySeries}</span>
                  <span>ready now</span>
                </div>
                <div className="flex items-center gap-1 text-gray-500">
                  <span className="font-medium">{seriesStats.totalSeries - seriesStats.readySeries}</span>
                  <span>coming soon</span>
                </div>
              </div>
            </div>
            
            {/* Clear Filters Button */}
            {(searchTerm || selectedCategory !== 'All') && (
              <button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedCategory('All')
                }}
                className="text-sm text-primary hover:text-primary/80 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Series Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {sortedSeries.map((series) => {
            const statusInfo = getStatusInfo(series.status)
            
            return (
              <motion.div
                key={series.id}
                whileHover={{ y: -4 }}
                className={`bg-gradient-to-br ${series.color} rounded-xl p-6 text-white hover:shadow-xl transition-all duration-300 cursor-pointer group relative h-full flex flex-col`}
                onClick={() => navigate(`/podcast-series/${series.id}`)}
              >
                {/* Status Badge */}
                <div className="absolute top-4 left-4 z-10">
                  <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 border ${statusInfo.color}`}>
                    {statusInfo.icon}
                    <span>{statusInfo.text}</span>
                  </div>
                </div>
                
                {/* Series Image */}
                <div className="mb-4 h-40 overflow-hidden rounded-lg">
                  <img 
                    src={series.image} 
                    alt={series.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <Headphones className="w-5 h-5 flex-shrink-0" />
                    <h3 className="text-lg font-bold line-clamp-1">{series.title}</h3>
                  </div>
                  
                  <p className="text-white/90 text-sm line-clamp-2 mb-4">
                    {series.description}
                  </p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-xs text-white/80">
                      <Play className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{series.episodeCount} episodes</span>
                      {series.status !== 'ready' && (
                        <span className="text-white/60">
                          ({series.episodesWithAudio} ready)
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-white/80">
                      <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{series.totalDuration}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-white/80">
                      <Star className="w-3.5 h-3.5 flex-shrink-0 fill-current" />
                      <span>{series.rating}</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-auto pt-4">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/podcast-series/${series.id}`)
                    }}
                    className="w-full flex items-center justify-center space-x-2 py-2.5 bg-white/30 rounded-lg hover:bg-white/40 transition-colors duration-200 text-sm font-medium"
                  >
                    <span>View Series</span>
                    <ChevronLeft className="w-4 h-4 rotate-180" />
                  </button>
                </div>
              </motion.div>
            )
          })}
        </div>

        {sortedSeries.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <Headphones className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No series found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm ? `No results for "${searchTerm}"` : 'Try adjusting your search criteria'}
            </p>
            <button
              onClick={() => {
                setSearchTerm('')
                setSelectedCategory('All')
              }}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200 font-medium"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default SeriesList